import React, { useState, useEffect } from 'react';
import { usersAPI, resourcesAPI, jobsAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import DocumentAttachments from '../components/DocumentAttachments';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, DollarSign, ChevronDown, ChevronUp, X, Save, Key, Paperclip } from 'lucide-react';

// Pay Rate Modal Component
const PayRateModal = ({ isOpen, onClose, resource, jobs, onSave }) => {
  const [payRates, setPayRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedRate, setExpandedRate] = useState(null);

  useEffect(() => {
    if (isOpen && resource) {
      loadPayRates();
    }
  }, [isOpen, resource]);

  const loadPayRates = async () => {
    setLoading(true);
    try {
      const res = await resourcesAPI.getPayRates(resource.id);
      setPayRates(res.data);
    } catch (error) {
      console.error('Failed to load pay rates:', error);
      setPayRates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRate = () => {
    setPayRates(prev => [...prev, {
      id: `new-${Date.now()}`,
      resource_id: resource.id,
      job_id: null,
      daily_rate: 0,
      hourly_rate: 0,
      accommodation_rate: 0,
      km_rate: 0,
      return_trip_rate: 0,
      isNew: true
    }]);
  };

  const handleRateChange = (index, field, value) => {
    setPayRates(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDeleteRate = async (index) => {
    const rate = payRates[index];
    if (rate.isNew) {
      setPayRates(prev => prev.filter((_, i) => i !== index));
    } else {
      if (!window.confirm('Delete this pay rate?')) return;
      try {
        await resourcesAPI.deletePayRate(rate.id);
        toast.success('Pay rate deleted');
        setPayRates(prev => prev.filter((_, i) => i !== index));
      } catch (error) {
        toast.error('Failed to delete pay rate');
      }
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      for (const rate of payRates) {
        await resourcesAPI.upsertPayRate(resource.id, {
          job_id: rate.job_id,
          daily_rate: parseFloat(rate.daily_rate) || 0,
          hourly_rate: parseFloat(rate.hourly_rate) || 0,
          accommodation_rate: parseFloat(rate.accommodation_rate) || 0,
          km_rate: parseFloat(rate.km_rate) || 0,
          return_trip_rate: parseFloat(rate.return_trip_rate) || 0,
        });
      }
      toast.success('Pay rates saved');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save pay rates');
    } finally {
      setSaving(false);
    }
  };

  const getJobName = (jobId) => {
    if (!jobId) return 'Default (All Projects)';
    const job = jobs.find(j => j.id === jobId);
    return job ? `${job.job_number} - ${job.name}` : 'Unknown Job';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Pay Rates for {resource?.name}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Set job-specific pay rates or a default rate for all jobs
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#194eb2]" />
            </div>
          ) : (
            <div className="space-y-4">
              {payRates.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No pay rates configured. Add a default rate or job-specific rates.
                </div>
              ) : (
                payRates.map((rate, index) => (
                  <div key={rate.id} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* Rate Header */}
                    <div 
                      className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer"
                      onClick={() => setExpandedRate(expandedRate === index ? null : index)}
                    >
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="font-medium text-slate-900">{getJobName(rate.job_id)}</p>
                          <p className="text-sm text-slate-500">
                            ${rate.daily_rate}/day • ${rate.hourly_rate}/hr • ${rate.km_rate}/km
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteRate(index); }}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {expandedRate === index ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Rate Details */}
                    {expandedRate === index && (
                      <div className="p-4 space-y-4 border-t">
                        {/* Job Selection */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Project
                          </label>
                          <select
                            value={rate.job_id || ''}
                            onChange={(e) => handleRateChange(index, 'job_id', e.target.value || null)}
                            className="input-base"
                          >
                            <option value="">Default (All Projects)</option>
                            {jobs.filter(j => j.is_active).map(job => (
                              <option key={job.id} value={job.id}>
                                {job.job_number} - {job.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Work Rates */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Daily Rate ($)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={rate.daily_rate}
                              onChange={(e) => handleRateChange(index, 'daily_rate', e.target.value)}
                              className="input-base font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Hourly Rate ($)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={rate.hourly_rate}
                              onChange={(e) => handleRateChange(index, 'hourly_rate', e.target.value)}
                              className="input-base font-mono"
                            />
                          </div>
                        </div>

                        {/* Expense Rates */}
                        <div className="pt-2 border-t">
                          <p className="text-sm font-medium text-slate-700 mb-3">Expense Rates</p>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm text-slate-600 mb-1">
                                Accommodation ($/day)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={rate.accommodation_rate}
                                onChange={(e) => handleRateChange(index, 'accommodation_rate', e.target.value)}
                                className="input-base font-mono"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-slate-600 mb-1">
                                Km Rate ($/km)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={rate.km_rate}
                                onChange={(e) => handleRateChange(index, 'km_rate', e.target.value)}
                                className="input-base font-mono"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-slate-600 mb-1">
                                Return Trip ($)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={rate.return_trip_rate}
                                onChange={(e) => handleRateChange(index, 'return_trip_rate', e.target.value)}
                                className="input-base font-mono"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}

              {/* Add Rate Button */}
              <button
                onClick={handleAddRate}
                className="w-full py-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-[#194eb2] hover:text-[#194eb2] transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Pay Rate
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-slate-50">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save All Rates
          </button>
        </div>
      </div>
    </div>
  );
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [resources, setResources] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPayRateModal, setShowPayRateModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'subcontractor',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showDocuments, setShowDocuments] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, resourcesRes, jobsRes] = await Promise.all([
        usersAPI.getAll(),
        resourcesAPI.getAll(),
        jobsAPI.getAll()
      ]);
      // Sort users alphabetically by name
      const sortedUsers = usersRes.data.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setUsers(sortedUsers);
      setResources(resourcesRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getResourceForUser = (userId) => {
    return resources.find(r => r.user_id === userId);
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        password: '',
        role: user.role,
        is_active: user.is_active,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'subcontractor',
        is_active: true,
      });
    }
    setShowModal(true);
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setShowDocuments(false);
    setError('');
  };

  const openPayRateModal = (resource) => {
    setSelectedResource(resource);
    setShowPayRateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const userData = {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        is_active: formData.is_active
      };

      if (formData.password) {
        userData.password = formData.password;
      }

      let userId;
      if (editingUser) {
        await usersAPI.update(editingUser.id, userData);
        userId = editingUser.id;
      } else {
        if (!formData.password) {
          setError('Password is required for new users');
          setSaving(false);
          return;
        }
        userData.password = formData.password;
        const response = await usersAPI.create(userData);
        userId = response.data.id;
      }

      // Create resource for subcontractors if not exists
      if (formData.role === 'subcontractor') {
        const existingResource = getResourceForUser(userId);
        if (!existingResource) {
          await resourcesAPI.create({
            user_id: userId,
            name: formData.name,
            pay_rate: 0,
            unit_type: 'days',
            is_active: formData.is_active
          });
        } else {
          // Update resource name and status
          await resourcesAPI.update(existingResource.id, {
            name: formData.name,
            is_active: formData.is_active
          });
        }
      }
      
      closeModal();
      fetchData();
      toast.success(editingUser ? 'User updated' : 'User created');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await usersAPI.delete(id);
      fetchData();
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (user) => {
    const newPassword = window.prompt(`Enter new password for ${user.name}:`, '');
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await usersAPI.resetPassword(user.id, newPassword);
      toast.success(`Password reset successfully for ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Users</h1>
            <p className="mt-1 text-slate-500">Manage user accounts and subcontractor pay rates</p>
          </div>
          <button
            onClick={() => openModal()}
            className="btn-primary flex items-center gap-2"
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        </div>

        {/* Users Table */}
        <div className="card-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-3 text-left">User</th>
                  <th className="px-6 py-3 text-left">Email</th>
                  <th className="px-6 py-3 text-left">Role</th>
                  <th className="px-6 py-3 text-center">Pay Rates</th>
                  <th className="px-6 py-3 text-center">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => {
                  const resource = getResourceForUser(user.id);
                  return (
                    <tr key={user.id} className="table-row">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#194eb2] rounded-full flex items-center justify-center">
                            <span className="text-white font-medium">
                              {user.name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <span className="font-medium text-slate-900">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{user.email}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin'
                            ? 'bg-purple-50 text-purple-700 border border-purple-200'
                            : 'bg-blue-50 text-blue-700 border border-blue-200'
                        }`}>
                          {user.role === 'admin' ? 'Admin' : 'Subcontractor'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {resource ? (
                          <button
                            onClick={() => openPayRateModal(resource)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#194eb2] bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                            data-testid={`pay-rates-${user.id}`}
                          >
                            <DollarSign className="w-4 h-4" />
                            Manage Rates
                          </button>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                            : 'bg-slate-50 text-slate-500 border border-slate-200'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleResetPassword(user)}
                            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-md"
                            title="Reset Password"
                            data-testid={`reset-password-${user.id}`}
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openModal(user)}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              {editingUser ? 'Edit User' : 'Add User'}
            </h3>
            
            {/* Tabs for Edit mode */}
            {editingUser && (
              <div className="flex border-b border-slate-200 mb-4">
                <button
                  type="button"
                  onClick={() => setShowDocuments(false)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    !showDocuments 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Details
                </button>
                <button
                  type="button"
                  onClick={() => setShowDocuments(true)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1 ${
                    showDocuments 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Paperclip className="w-4 h-4" />
                  Documents
                </button>
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            
            {/* Documents Tab */}
            {showDocuments && editingUser ? (
              <div className="py-2">
                <DocumentAttachments
                  entityType="user"
                  entityId={editingUser.id}
                />
              </div>
            ) : (
              /* Details Form */
              <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-base"
                  data-testid="user-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="input-base"
                  data-testid="user-email-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password {editingUser ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  className="input-base"
                  data-testid="user-password-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input-base"
                >
                  <option value="subcontractor">Subcontractor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {formData.role === 'subcontractor' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Pay Rates:</strong> After creating the user, use the "Manage Rates" button to configure job-specific pay rates including daily rate, hourly rate, accommodation, km rate, and return trip rates.
                  </p>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-[#194eb2] focus:ring-[#194eb2] border-slate-300 rounded"
                />
                <label htmlFor="is_active" className="text-sm text-slate-700">
                  Active account
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-user-btn"
                >
                  {saving ? 'Saving...' : editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      {/* Pay Rate Modal */}
      <PayRateModal
        isOpen={showPayRateModal}
        onClose={() => setShowPayRateModal(false)}
        resource={selectedResource}
        jobs={jobs}
        onSave={fetchData}
      />
    </DashboardLayout>
  );
};

export default UsersPage;
